import axios, { Axios } from 'axios';
import mitt from 'mitt';
import { Config, Container, Scope } from 'typescript-ioc';
import { getAppConfig } from '@client/core/config/get-app-config';
import { Observer, ObserverImpl } from '@client/core/dom/observer';
import { EventBus, Events, EventsMap } from '@client/core/events/event-bus';
import { defaultHttpExceptionCatch } from '@client/core/exception/default-http-exception-catch';
import { uri } from '@client/core/routing/uri';
import { error } from '@client/core/utils/logging';
import { ActivityLogCommentDataSource } from '@client/features/activity-log/data-sources/activity-log-comment-data-source';
import { ActivityLogDataSource } from '@client/features/activity-log/data-sources/activity-log-data-source';
import { PaymentInformationDataSource } from '@client/features/billing/data-sources/payment-information-data-source';
import { PlanChangeProjectionDataSource } from '@client/features/billing/data-sources/plan-change-projection-data-source';
import { SubscriptionPlanDataSource } from '@client/features/billing/data-sources/subscription-plan-data-source';
import { CompDataSource } from '@client/features/comps/data-sources/comp-data-source';
import { ConstructionMaterialCategoryDataSource } from '@client/features/material-catalog/data-sources/construction-material-category-data-source';
import { ConstructionMaterialDataSource } from '@client/features/material-catalog/data-sources/construction-material-data-source';
import { ConstructionMaterialSupplierDataSource } from '@client/features/material-catalog/data-sources/construction-material-supplier-data-source';
import { MemberAccountDataSource } from '@client/features/member-account/data-sources/member-account-data-source';
import { NotificationDataSource } from '@client/features/notifications/data-sources/notification-data-source';
import { OgMetaFieldsDataSource } from '@client/features/og-meta-fields/data-sources/og-meta-fields-data-source';
import { ProjectDataSource } from '@client/features/project/data-sources/project-data-source';
import { PropertyDataDataSource } from '@client/features/property-data/data-sources/property-data-data-source';
import { PropertySearchDataSource } from '@client/features/property-search/data-sources/property-search-data-source';
import { RepairItemDataSource } from '@client/features/repair-item/data-sources/repair-item-data-source';
import { ShareLinkDataSource } from '@client/features/share-link/data-sources/share-link-data-source';
import { WorkspaceCompanyDataSource } from '@client/features/workspace-company/data-sources/workspace-company-data-source';
import { ActiveProjectDashboardDataSource } from '@client/features/dashboards/store/active-project-dashboard-data-source';
import { CompanyProfileDataSource } from '@client/features/company-profile/data-sources/company-profile-data-source';
import { buildClientHeader } from '@client/core/http/build-client-header';

const { csrfToken } = getAppConfig();

// eslint-disable-next-line @typescript-eslint/ban-types
type Binding<T> = Function & {
  prototype: T;
};

/**
 * A cache of all the bindings that have been registered.
 */
const bindingCache = new Map<Binding<any>, boolean>();

/**
 * Bind a value to the container.
 * This is a simple proxy that will cache the binding so we can know if it's been registered.
 * It will throw an error if the binding is not registered.
 */
export function bind<T>(source: Binding<T>): Config {
  bindingCache.set(source, true);

  return Container.bind(source);
}

/**
 * Get a value from the container.
 */
export function container<T>(source: Binding<T>): T {
  if (!bindingCache.has(source)) {
    error(`${source.name} is not registered in the Container. Did you forget to register it?`);
  }

  return Container.get(source);
}

bind(Axios).factory(() => {
  const instance = axios.create({
    baseURL: uri('/api'),
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
      ...buildClientHeader(),
    },
    withCredentials: true,
  });

  // Add custom Error handling
  instance.interceptors.response.use(
    // Callback when status code in range of 2xx
    function (response) {
      return response;
    },
    // Callback when status code outside range of 2xx
    function (error) {
      return Promise.reject(defaultHttpExceptionCatch(error));
    },
  );

  return instance;
});

bind(ConstructionMaterialDataSource).factory(() => new ConstructionMaterialDataSource(Container.get(Axios)));
bind(ConstructionMaterialCategoryDataSource).factory(
  () => new ConstructionMaterialCategoryDataSource(Container.get(Axios)),
);
bind(ConstructionMaterialSupplierDataSource).factory(
  () => new ConstructionMaterialSupplierDataSource(Container.get(Axios)),
);
bind(RepairItemDataSource).factory(() => new RepairItemDataSource(Container.get(Axios)));
bind(OgMetaFieldsDataSource).factory(() => new OgMetaFieldsDataSource(Container.get(Axios)));
bind(ShareLinkDataSource).factory(() => new ShareLinkDataSource(Container.get(Axios)));
bind(PropertyDataDataSource).factory(() => new PropertyDataDataSource(Container.get(Axios)));
bind(PaymentInformationDataSource).factory(() => new PaymentInformationDataSource(Container.get(Axios)));
bind(WorkspaceCompanyDataSource).factory(() => new WorkspaceCompanyDataSource(Container.get(Axios)));
bind(ProjectDataSource).factory(() => new ProjectDataSource(Container.get(Axios)));
bind(CompDataSource).factory(() => new CompDataSource(Container.get(Axios)));
bind(ActivityLogDataSource).factory(() => new ActivityLogDataSource(Container.get(Axios)));
bind(ActivityLogCommentDataSource).factory(() => new ActivityLogCommentDataSource(Container.get(Axios)));
bind(PropertySearchDataSource).factory(() => new PropertySearchDataSource(Container.get(Axios)));
bind(PlanChangeProjectionDataSource).factory(() => new PlanChangeProjectionDataSource(Container.get(Axios)));
bind(SubscriptionPlanDataSource).factory(() => new SubscriptionPlanDataSource(Container.get(Axios)));
bind(NotificationDataSource).factory(() => new NotificationDataSource(Container.get(Axios)));
bind(MemberAccountDataSource).factory(() => new MemberAccountDataSource(Container.get(Axios)));
bind(ActiveProjectDashboardDataSource).factory(() => new ActiveProjectDashboardDataSource(Container.get(Axios)));
bind(CompanyProfileDataSource).factory(() => new CompanyProfileDataSource(Container.get(Axios)));

bind(EventBus)
  .factory(() => mitt<EventsMap>())
  .scope(Scope.Singleton);

bind(IntersectionObserver)
  .factory(() => {
    return new IntersectionObserver(function (entries) {
      const eventBus = Container.get(EventBus);

      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          eventBus.emit(Events.ObserverIntersected, entry.target);
        }
      });
    });
  })
  .scope(Scope.Singleton);

bind(Observer)
  .factory(() => new ObserverImpl(Container.get(IntersectionObserver)))
  .scope(Scope.Singleton);
